import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faChevronDown, faChevronRight } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";

import { ToDoStatus } from "../../ToDo/ToDoList/enum/ToDoStatus.enum";
import { SubTaskInfo, TaskInfo } from "../../TasksAndSubTasks/schema/tasks-and-subtasks";
import SwimlaneColumn from "./SwimlaneColumn";

interface SwimlaneRowProps {
	tagName: string;
	tagId: number | null;
	items: (TaskInfo | SubTaskInfo)[];
	showTaskDetails: (taskId: number) => void;
	showSubTaskDetails: (subTaskId: number) => void;
}

export default function SwimlaneRow({
	tagName,
	items,
	showTaskDetails,
	showSubTaskDetails,
}: SwimlaneRowProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);
	const [isCollapsed, setIsCollapsed] = useState(false);

	const groupByParent = (filtered: (TaskInfo | SubTaskInfo)[]) => {
		const parentTasks = filtered.filter((item) => item.isTask) as TaskInfo[];
		const subTaskItems = filtered.filter((item) => !item.isTask) as SubTaskInfo[];

		const attachedSubTaskIds = new Set<number>();

		const grouped = parentTasks.flatMap((parent) => {
			const children = subTaskItems.filter((sub) => sub.taskId === parent.id);

			children.forEach((c) => attachedSubTaskIds.add(c.id!));

			return [parent, ...children];
		});

		const orphans = subTaskItems.filter((sub) => !attachedSubTaskIds.has(sub.id!));

		return [...grouped, ...orphans];
	};

	const { toDo, inProgress, done, qa } = useMemo(() => {
		const toDo = groupByParent(
			items.filter((item) => item.status === ToDoStatus.TO_DO && item?.id !== undefined)
		);
		const inProgress = groupByParent(
			items.filter((item) => item.status === ToDoStatus.IN_PROGRESS && item?.id !== undefined)
		);

		const qa = groupByParent(
			items.filter((item) => item.status === ToDoStatus.QA && item?.id !== undefined)
		);
		const done = groupByParent(
			items.filter((item) => item.status === ToDoStatus.DONE && item?.id !== undefined)
		);

		return { toDo, inProgress, done, qa };
	}, [items]);

	const totalCount = items.length;
	const doneCount = done.length;

	return (
		<div className="border border-primary-gray-lightest rounded-2xl overflow-hidden">
			<button
				className="w-full flex items-center gap-3 px-4 py-3 bg-surface hover:bg-gray-100 transition-colors text-left"
				type="button"
				onClick={() => setIsCollapsed(!isCollapsed)}
			>
				<Icon
					className="w-3 h-3 text-primary-blue-dark"
					icon={isCollapsed ? faChevronRight : faChevronDown}
				/>
				<span className="text-[14px] font-semibold text-primary-blue-dark">{tagName}</span>
				<span className="border border-primary-gray-lightest bg-primary-gray-lightest rounded-[4px] px-1.5 h-5 flex items-center justify-center text-text text-[12px] font-medium">
					{totalCount}
				</span>
				<span className="text-[12px] text-subtle ml-auto">
					{doneCount}/{totalCount} {ts("tasks-completed")}
				</span>
			</button>

			{!isCollapsed && (
				<div className="p-3">
					<div className="grid grid-cols-4 gap-3">
						<SwimlaneColumn
							allItems={items}
							data={toDo}
							showSubTaskDetails={showSubTaskDetails}
							showTaskDetails={showTaskDetails}
							status={ToDoStatus.TO_DO}
						/>
						<SwimlaneColumn
							allItems={items}
							data={inProgress}
							showSubTaskDetails={showSubTaskDetails}
							showTaskDetails={showTaskDetails}
							status={ToDoStatus.IN_PROGRESS}
						/>
						<SwimlaneColumn
							allItems={items}
							data={qa}
							showSubTaskDetails={showSubTaskDetails}
							showTaskDetails={showTaskDetails}
							status={ToDoStatus.QA}
						/>
						<SwimlaneColumn
							allItems={items}
							data={done}
							showSubTaskDetails={showSubTaskDetails}
							showTaskDetails={showTaskDetails}
							status={ToDoStatus.DONE}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
